import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ITabPanelHeader } from '@design-system/components/m-tab-view';
import { TypePillSize } from '@design-system/components/m-type-pill';
import { ModalBaseComponent } from '@portal/app/dashboard/context-modal/components/base-modal.component';
import { MediaService } from '@portal/app/dashboard/context-modal/services/media.service';
import {
  ModalService,
  TACTIC_MODAL_PARAMS
} from '@portal/app/dashboard/context-modal/services/modal.service';
import { ContextModalParams } from '@portal/app/dashboard/context-modal/context-model.types';
import { DimensionOption } from '@portal/app/dashboard/context-modal/services/link.service';
import { DataResponse } from '@portal/app/shared/types';
import { selectStore } from '@portal/app/store/app.selectors';
import { AppState } from '@portal/app/store/app.state';
import { Store } from '@ngrx/store';
import { LinkService } from '@portal/app/dashboard/context-modal/services/link.service';
import { MIMQueriesService } from '@portal/app/shared/services/queries/mim-queries.service';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { SPEND_MODEL } from '@portal/app/shared/constants';

@Component({
  selector: 'portal-context-tactic-modal',
  templateUrl: './tactic-modal.component.html',
  providers: [ModalService, MediaService],
  standalone: false
})
export class TacticModalComponent extends ModalBaseComponent implements OnInit {
  modalService = inject(ModalService);
  mediaService = inject(MediaService);
  private mimQueries = inject(MIMQueriesService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store<AppState>);
  linkService = inject(LinkService);
  public SPEND_MODEL = SPEND_MODEL;

  mimVersionQuery = injectQuery(() => {
    return this.mimQueries.mimVersion();
  });

  isTacticModalVisible = signal(false);
  rowSignal = signal<ContextModalParams>({} as ContextModalParams);

  mimVersion = computed(() => this.mimVersionQuery.data()?.mimVersion || 1);

  rollUpConversionTypes: string[] = [];
  isRollUpConversionType = false;

  typePillSize = TypePillSize;

  @Input()
  set row(value: DataResponse | ContextModalParams) {
    this.rowSignal.set(value as ContextModalParams);
  }

  previous = new EventEmitter<ContextModalParams>();
  next = new EventEmitter<ContextModalParams>();

  hasBackButton = false;

  currentRow = this.modalService.currentRow;
  comparePeriod = this.modalService.comparePreviousPeriod;

  isMediaLoading = this.mediaService.isLoading;
  mediaTable = this.mediaService.table;
  isMediaDataAvailable = this.mediaService.isDataAvailable;

  tactics = this.linkService.tactics;
  tacticsOptions = this.linkService.tacticsOptions;

  ctModelInfo = this.linkService.ctModelInfo;

  public tabPanelHeaders: ITabPanelHeader[] = [
    { header: 'Summary', disabled: false, showCount: false, count: 0 },
    { header: 'Metrics', disabled: false, showCount: false, count: 0 },
    { header: 'Campaigns', disabled: false, showCount: false, count: 0 }
  ];

  title = computed(() => this.modalService.modalParams().tactic || '');
  pill = computed(() => this.modalService.modalParams().channel);

  protected get isVisible(): boolean {
    return this.isTacticModalVisible();
  }

  constructor() {
    super();
    this.store.select(selectStore).subscribe((state: AppState) => {
      this.rollUpConversionTypes = state.dashboard.rollupConversionTypes;
      this.isRollUpConversionType = this.rollUpConversionTypes.includes(
        this.modalService.conversionType()
      );
    });
    effect(() => {
      const count = this.mediaService.response().data?.length;
      const tab = this.tabPanelHeaders[2];
      if (tab) {
        this.setCount(count, tab);
      }
    });
  }

  setCount(count: number, tab: ITabPanelHeader): void {
    if (count > 0) {
      tab.showCount = true;
      tab.count = count;
    } else {
      tab.showCount = false;
    }
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params.modal === 'tactic' && params.channel && params.tactic) {
        const updatedParams = this.modalService.buildModalParams(
          params,
          TACTIC_MODAL_PARAMS
        );
        this.modalService.setCurrentRow(updatedParams);
        this.isRollUpConversionType = this.rollUpConversionTypes.includes(
          updatedParams.conversionType as string
        );
        this.rowSignal.set(updatedParams);
        this.isTacticModalVisible.set(true);
      } else {
        this.isTacticModalVisible.set(false);
      }
    });
    this.modalService.setCurrentRow(this.rowSignal());
  }

  nextAction(): void {
    const row = this.linkService.findNextTactic(
      this.currentRow(),
      this.hasBackButton ? this.tactics() : undefined
    );
    this.setCurrentRow(row);
  }

  previousAction(): void {
    const row = this.linkService.findPrevTactic(
      this.currentRow(),
      this.hasBackButton ? this.tactics() : undefined
    );
    this.setCurrentRow(row);
  }

  selectAction(row: DimensionOption): void {
    const { channel, tactic, dateStart, dateStop } = row;
    this.setCurrentRow({ channel, tactic, dateStart, dateStop });
  }

  override onCloseModal(): void {
    super.onCloseModal();
    this.router.navigate([], {
      queryParams: this.modalService.buildCloseParams(TACTIC_MODAL_PARAMS),
      queryParamsHandling: 'merge'
    });
    this.isTacticModalVisible.set(false);
  }

  private setCurrentRow(row: ContextModalParams | null | undefined): void {
    if (row) {
      this.modalService.setCurrentRow(row);
    }
  }
}
